import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import Modal from '../../UI/Modal';
import Button from '../../UI/Button';
import Input from '../../UI/Input';
import currencyService from '../../../services/currency/currencyService';
import {
  stripWholeDigits,
  formatThousandsDots,
  wholeDigitsForParse,
  countDigitsBeforeCaret,
  caretPositionForDigitsLeft,
} from '../../../utils/priceFormat';
import './ProductEditModal.css';

const ProductEditModal = ({
  isOpen,
  onClose,
  product,
  onSave,
  loading = false
}) => {
  const [title, setTitle] = useState('');
  const [priceLira, setPriceLira] = useState('');
  const [priceKurus, setPriceKurus] = useState('');
  const [priceEuro, setPriceEuro] = useState('');
  const [priceCent, setPriceCent] = useState('');
  const [eurRate, setEurRate] = useState(null);
  const priceEuroRef = useRef(null);
  const priceLiraRef = useRef(null);
  const pendingLiraCaretRef = useRef(null);
  const pendingEuroCaretRef = useRef(null);

  const sanitizeFractionDigits = (raw) => String(raw ?? '').replace(/\D/g, '').slice(0, 2);

  // EUR kurunu al
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    currencyService.getCurrencyRates()
      .then((rates) => {
        if (!cancelled && rates?.EUR?.buying) {
          setEurRate(rates.EUR.buying);
        }
      })
      .catch((err) => {
        console.warn('ProductEditModal: EUR kuru alınamadı', err.message);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  // Modal açıldığında TL alanlarını ürünün fiyatından doldur, EUR'u hesapla
  useEffect(() => {
    if (product && isOpen) {
      setTitle(product.title);
      const [lira, kurus] = product.variants[0].price.split('.');
      setPriceLira(formatThousandsDots(lira));
      setPriceKurus(kurus || '00');

      // EUR'u kurdan hesapla (kur geldiyse)
      if (eurRate) {
        const tl = parseFloat(`${wholeDigitsForParse(lira)}.${kurus || '00'}`);
        if (!isNaN(tl)) {
          const euro = tl / eurRate;
          const [eInt, eFrac] = euro.toFixed(2).split('.');
          setPriceEuro(formatThousandsDots(eInt));
          setPriceCent(eFrac);
        }
      }

      setTimeout(() => {
        if (priceEuroRef.current) {
          priceEuroRef.current.focus();
          priceEuroRef.current.select();
        }
      }, 100);
    }
  }, [product, isOpen, eurRate]);

  useLayoutEffect(() => {
    const pos = pendingLiraCaretRef.current;
    if (pos != null && priceLiraRef.current) {
      priceLiraRef.current.setSelectionRange(pos, pos);
      pendingLiraCaretRef.current = null;
    }
  }, [priceLira]);

  useLayoutEffect(() => {
    const pos = pendingEuroCaretRef.current;
    if (pos != null && priceEuroRef.current) {
      priceEuroRef.current.setSelectionRange(pos, pos);
      pendingEuroCaretRef.current = null;
    }
  }, [priceEuro]);

  // TL kullanıcı tarafından değişti → EUR'u hesapla
  const handleLiraChange = (e) => {
    const el = e.target;
    const sel = el.selectionStart ?? 0;
    const digitsLeft = countDigitsBeforeCaret(el.value, sel);
    const newFormatted = formatThousandsDots(el.value);
    pendingLiraCaretRef.current = caretPositionForDigitsLeft(newFormatted, digitsLeft);
    setPriceLira(newFormatted);
    if (!eurRate) return;
    const tl = parseFloat(`${wholeDigitsForParse(el.value)}.${priceKurus || '00'}`);
    if (!isNaN(tl)) {
      const euro = tl / eurRate;
      const [eInt, eFrac] = euro.toFixed(2).split('.');
      setPriceEuro(formatThousandsDots(eInt));
      setPriceCent(eFrac);
    }
  };

  const handleKurusChange = (value) => {
    const v = sanitizeFractionDigits(value);
    setPriceKurus(v);
    if (!eurRate) return;
    const tl = parseFloat(`${wholeDigitsForParse(priceLira)}.${v || '00'}`);
    if (!isNaN(tl)) {
      const euro = tl / eurRate;
      const [eInt, eFrac] = euro.toFixed(2).split('.');
      setPriceEuro(formatThousandsDots(eInt));
      setPriceCent(eFrac);
    }
  };

  // EUR kullanıcı tarafından değişti → TL'yi hesapla
  const handleEuroChange = (e) => {
    const el = e.target;
    const sel = el.selectionStart ?? 0;
    const digitsLeft = countDigitsBeforeCaret(el.value, sel);
    const newFormatted = formatThousandsDots(el.value);
    pendingEuroCaretRef.current = caretPositionForDigitsLeft(newFormatted, digitsLeft);
    setPriceEuro(newFormatted);
    if (!eurRate) return;
    const eur = parseFloat(`${wholeDigitsForParse(el.value)}.${priceCent || '00'}`);
    if (!isNaN(eur)) {
      const tl = eur * eurRate;
      const [tInt, tFrac] = tl.toFixed(2).split('.');
      setPriceLira(formatThousandsDots(tInt));
      setPriceKurus(tFrac);
    }
  };

  const handleCentChange = (value) => {
    const v = sanitizeFractionDigits(value);
    setPriceCent(v);
    if (!eurRate) return;
    const eur = parseFloat(`${wholeDigitsForParse(priceEuro)}.${v || '00'}`);
    if (!isNaN(eur)) {
      const tl = eur * eurRate;
      const [tInt, tFrac] = tl.toFixed(2).split('.');
      setPriceLira(formatThousandsDots(tInt));
      setPriceKurus(tFrac);
    }
  };

  const handleSave = () => {
    const whole = wholeDigitsForParse(priceLira);
    const finalPrice = `${whole}.${priceKurus.padEnd(2, '0')}`;
    const updatedData = {
      product: {
        title: title,
        variants: [
          {
            id: product.variants[0].id,
            price: finalPrice,
          },
        ],
      },
    };
    onSave(updatedData);
  };

  const handleClose = () => {
    setTitle('');
    setPriceLira('');
    setPriceKurus('');
    setPriceEuro('');
    setPriceCent('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Fiyatı Düzenle"
      size="mdPlus"
    >
      <div className="product-edit-form">
        <Input
          label="Ürün Başlığı"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ürün başlığını girin"
          required
          autoComplete="off"
        />

        <div className="price-currency-block">
          <div className="price-currency-row">
            <div className="price-box">
              <label className="price-label">Fiyat (Euro)</label>
              <div className="price-inputs">
                <div className="price-input-field price-input-field--whole">
                  <Input
                    ref={priceEuroRef}
                    type="text"
                    inputMode="numeric"
                    value={priceEuro}
                    onChange={handleEuroChange}
                    placeholder="0"
                    className="price-input-whole"
                    size="md"
                    disabled={!eurRate}
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                <span className="price-separator">,</span>
                <div className="price-input-field price-input-field--fraction">
                  <Input
                    type="number"
                    value={priceCent}
                    onChange={(e) => handleCentChange(e.target.value)}
                    placeholder="00"
                    className="price-input-fraction"
                    size="md"
                    disabled={!eurRate}
                    autoComplete="off"
                  />
                </div>
                <span className="currency-symbol">€</span>
              </div>
              {!eurRate && (
                <span className="price-hint">Kur bilgisi henüz yüklenmedi…</span>
              )}
            </div>

            <div className="price-box">
              <label className="price-label">Fiyat (TL)</label>
              <div className="price-inputs">
                <div className="price-input-field price-input-field--whole">
                  <Input
                    ref={priceLiraRef}
                    type="text"
                    inputMode="numeric"
                    value={priceLira}
                    onChange={handleLiraChange}
                    placeholder="0"
                    className="price-input-whole"
                    size="md"
                    autoComplete="off"
                  />
                </div>
                <span className="price-separator">,</span>
                <div className="price-input-field price-input-field--fraction">
                  <Input
                    type="number"
                    value={priceKurus}
                    onChange={(e) => handleKurusChange(e.target.value)}
                    placeholder="00"
                    className="price-input-fraction"
                    size="md"
                    autoComplete="off"
                  />
                </div>
                <span className="currency-symbol">₺</span>
              </div>
            </div>
          </div>
          {eurRate && (
            <p className="price-rate-note">
              1 € = {eurRate.toFixed(2)} ₺ (sadece TL değeri Shopify'a kaydedilir)
            </p>
          )}
        </div>

        <div className="modal-actions">
          <Button
            variant="success"
            onClick={handleSave}
            loading={loading}
            disabled={!title.trim() || stripWholeDigits(priceLira) === ''}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            İptal
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ProductEditModal;
